import { Gift, Menu, MessageCircle, ShoppingCart, User, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cachedApi } from '../../utils/globalCache';
import { useAuth } from '../../contexts/AuthContext';
// import { useCart } from '../../contexts/CartContext'; // Hidden temporarily - cart disabled
import { useLanguage } from '../../contexts/LanguageContext';

const Header = () => {
  const { user, logout } = useAuth();
  // const { cartItems } = useCart(); // Hidden temporarily - cart disabled
  const location = useLocation();
  const { t } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Navigation based on user role
  const navigation = user?.role === 'admin' ? [
    // Admin navigation - show useful admin functions
    { name: 'Dashboard', href: '/admin/dashboard' },
    { name: 'Users', href: '/admin/users' },
    { name: 'Orders', href: '/admin/orders' },
    { name: 'Events', href: '/admin/events' },
    { name: 'Support', href: '/messages' },
  ] : (user?.role === 'vendor' || user?.role === 'technician') ? [
    // Vendor/Technician navigation - show vendor-specific functions
    { name: 'Dashboard', href: '/vendor-dashboard?section=dashboard&tab=overview' },
    { name: 'My Jobs', href: '/vendor-dashboard?section=assignments&tab=pending' },
    { name: 'Earnings', href: '/vendor-dashboard?section=earnings&tab=earnings-overview' },
    { name: 'Profile', href: '/vendor-dashboard?section=account&tab=profile' },
  ] : [
    // Customer/Public navigation
    { name: t('home'), href: '/' },
    { name: t('services'), href: '/services' },
    { name: 'Elderly Care', href: '/elderly-care' },
    { name: 'Events', href: '/events' },
    // Show subscription link for customers and referral usersu
    ...((user?.role === 'customer' || user?.role === 'referral') ? [{
      name: 'Subscription Plans',
      href: '/subscription'
    }] : []),
    { name: 'FAQ', href: '/faq' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact Us', href: '/contact' },
  ];

  // Admin navigation items (only show for admin users)
  const adminNavigation = user?.role === 'admin' ? [
    { name: 'More', href: '/admin/dashboard', isDropdown: true, items: [
      { name: 'CEA Verification', href: '/admin/cea-verification' },
      { name: 'Inspection Management', href: '/admin/inspections' },
      { name: 'Email Management', href: '/admin/email-management' },
      { name: 'Homepage Settings', href: '/admin/homepage' },
      { name: 'FAQ Management', href: '/admin/faqs' },
    ]}
  ] : [];

  // Cache for API responses
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [cachedUnreadCount, setCachedUnreadCount] = useState(0);
  const CACHE_DURATION = 300000; // 5 minute cache to reduce API calls

  // Fetch unread messages count with caching
  const fetchUnreadCount = useCallback(async (forceRefresh = false) => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    console.log('[Header] fetchUnreadCount called, forceRefresh:', forceRefresh, 'pathname:', location.pathname);

    // Use cached data if available and not expired (unless forceRefresh)
    const now = Date.now();
    if (!forceRefresh && (now - lastFetchTime < CACHE_DURATION)) {
      console.log('[Header] Using cached unread count:', cachedUnreadCount);
      setUnreadCount(cachedUnreadCount);
      return;
    }

    try {
      // Use cached API call to prevent excessive requests
      const response = await cachedApi.getConversations(user.id, user.role, forceRefresh);

      const conversations = response.conversations || [];
      const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

      console.log('[Header] Fetched unread count:', totalUnread, 'conversations:', conversations.length);

      // Update cache
      setCachedUnreadCount(totalUnread);
      setLastFetchTime(now);
      setUnreadCount(totalUnread);

    } catch (error) {
      console.error('Failed to fetch unread count:', error);

      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        if (cachedUnreadCount !== null) {
          setUnreadCount(cachedUnreadCount);
        }
        return;
      }

      // Use cached data on error if available
      if (cachedUnreadCount > 0) {
        setUnreadCount(cachedUnreadCount);
      }
    }
  }, [user, lastFetchTime, cachedUnreadCount, location.pathname]);

  // Hide badge when on messages page
  const handleMessagesClick = () => {
    // Immediately clear the unread count when clicking messages link
    setUnreadCount(0);
    setCachedUnreadCount(0);
  };

  const handleLogout = () => {
    logout();
  };

  // Check if we're on the messages page
  const isOnMessagesPage = location.pathname === '/messages' || location.pathname.startsWith('/messages/');

  // Debug log
  useEffect(() => {
    console.log('[Header] Location changed:', location.pathname, 'isOnMessagesPage:', isOnMessagesPage, 'unreadCount:', unreadCount);
  }, [location.pathname, isOnMessagesPage, unreadCount]);

  // Fetch unread count on mount and periodically (only when page is visible)
  useEffect(() => {
    if (!user) return;

    // Only fetch unread count on mount if not on messages page
    if (!isOnMessagesPage) {
      fetchUnreadCount(true);
    }

    // Increased frequency: every 5 minutes instead of 2 minutes to reduce server load
    const interval = setInterval(() => {
      // Only fetch if page is visible and not on messages page
      if (!document.hidden && !isOnMessagesPage) {
        fetchUnreadCount();
      }
    }, 300000); // 5 minutes

    // Listen for page visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden && !isOnMessagesPage) {
        // Page became visible, fetch fresh data with debounce
        setTimeout(() => {
          fetchUnreadCount(true); // Force refresh after short delay
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchUnreadCount, isOnMessagesPage]);

  // Refresh unread count when navigating away from messages page
  useEffect(() => {
    // Check if we just left the messages page
    const prevPath = sessionStorage.getItem('prevPath');
    const currentPath = location.pathname;
    const prevWasMessages = prevPath === '/messages' || (prevPath && prevPath.startsWith('/messages/'));
    const currentIsMessages = currentPath === '/messages' || currentPath.startsWith('/messages/');

    if (prevWasMessages && !currentIsMessages && user) {
      // Just left messages page, refresh unread count after a short delay
      // Delay ensures messages are marked as read on server
      setTimeout(() => {
        fetchUnreadCount(true);
      }, 500);
    }

    // Store current path for next navigation
    sessionStorage.setItem('prevPath', currentPath);
  }, [location.pathname, user, fetchUnreadCount]);

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-white shadow-md">
      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="SwiftFixPro"
              className="h-20 w-auto border-0"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Regular Navigation */}
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-lg font-medium text-gray-700 hover:text-orange-600 transition-colors ${
                  location.pathname === item.href ? 'text-orange-600 font-semibold' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Admin Navigation Dropdown */}
            {adminNavigation.map((item) => (
              <div key={item.name} className="relative group">
                <button className="flex items-center text-lg font-medium text-gray-700 hover:text-orange-600 transition-colors">
                  {item.name}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 top-full mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-[9999] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all border border-gray-200">
                  {item.items.map((subItem) => (
                    <Link
                      key={subItem.name}
                      to={subItem.href}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {subItem.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center space-x-4">

            {/* Messages - Show for all logged in users */}
            {user && (
              <Link
                to="/messages"
                onClick={handleMessagesClick}
                className="relative flex items-center text-gray-700 hover:text-orange-600"
                title={user.role === 'admin' ? 'Support Messages' : 'Messages'}
              >
                <MessageCircle className="w-6 h-6" />
                {unreadCount > 0 && !isOnMessagesPage && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            )}

            {/* Cart - Hidden temporarily */}
            {/* <Link to="/cart" className="relative flex items-center text-gray-700 hover:text-orange-600">
              <ShoppingCart className="w-6 h-6" />
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItems.length}
                </span>
              )}
            </Link> */}

            {/* User Menu */}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-orange-600"
                >
                  <User className="w-5 h-5" />
                  <div className="hidden md:block">
                    <span className="block text-sm">{user.firstName || user.name}</span>
                    <span className="block text-xs text-gray-500 capitalize">{user.role}</span>
                  </div>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-2 z-[9999] border border-gray-200">
                    {/* Membership Info */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            {(user.role === 'vendor' || user.role === 'technician') ? 'Membership' : user.role === 'admin' ? 'Access Level' : 'Account'}
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {(user.role === 'vendor' || user.role === 'technician') ? 'Professional Plan' :
                             user.role === 'admin' ? 'Administrator' :
                             'Standard Member'}
                          </p>
                        </div>
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            user.status === 'ACTIVE' ? 'bg-green-400' : 'bg-yellow-400'
                          }`}></div>
                          <span className={`text-xs font-medium ${
                            user.status === 'ACTIVE' ? 'text-green-600' : 'text-yellow-600'
                          }`}>
                            {user.status === 'ACTIVE' ? 'Active' : 'Pending'}
                          </span>
                        </div>
                      </div>
                      {(user.role === 'vendor' || user.role === 'technician') && user.status === 'ACTIVE' && (
                        <p className="text-xs text-gray-500 mt-1">Next billing: Feb 15, 2025</p>
                      )}
                      {user.status === 'PENDING' && (
                        <p className="text-xs text-yellow-600 mt-1">Account pending approval</p>
                      )}
                    </div>

                    <Link
                      to={
                        user.role === 'admin' ? '/admin/dashboard' :
                        (user.role === 'vendor' || user.role === 'technician') ? '/vendor-dashboard?section=dashboard&tab=overview' :
                        (user.role === 'customer' || user.role === 'referral') ? '/dashboard' :
                        '/dashboard'
                      }
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                      }}
                    >
                      {t('dashboard')} {user.role === 'admin' && '(Admin)'} {(user.role === 'vendor' || user.role === 'technician') && '(Vendor)'} {user.role === 'referral' && '(Referral)'}
                    </Link>

                    {/* Customer and Referral-specific menu items */}
                    {(user.role === 'customer' || user.role === 'referral') && (
                      <>
                        <Link
                          to="/subscription/dashboard"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Manage Subscription
                        </Link>
                      </>
                    )}

                    {(user.role === 'customer' || user.role === 'referral') && (
                      <>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          {t('profile')}
                        </Link>
                        <Link
                          to={user.role === 'referral' ? '/referral-dashboard' : '/referral'}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          {user.role === 'referral' ? 'Referral Dashboard' : 'Referrals'}
                        </Link>
                      </>
                    )}

                    {/* Admin-specific menu items */}
                    {user.role === 'admin' && (
                      <>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          {t('profile')}
                        </Link>
                      </>
                    )}

                    {/* Vendor-specific menu items */}
                    {(user.role === 'vendor' || user.role === 'technician') && (
                      <>
                        <Link
                          to="/vendor-dashboard?section=account&tab=profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          {t('profile')}
                        </Link>
                        <Link
                          to="/vendor-dashboard?section=assignments&tab=pending"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          My Jobs
                        </Link>
                        <Link
                          to="/messages"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Messages
                        </Link>
                        <Link
                          to="/vendor-dashboard?section=earnings&tab=earnings-overview"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Earnings
                        </Link>
                      </>
                    )}

                    {/* Other user roles menu items (exclude referral since they're handled above) */}
                    {user.role !== 'vendor' && user.role !== 'technician' && user.role !== 'customer' && user.role !== 'referral' && user.role !== 'admin' && (
                      <>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            setIsUserMenuOpen(false);
                          }}
                        >
                          {t('profile')}
                        </Link>
                      </>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsUserMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-orange-600 transition-colors"
                >
                  {t('login')}
                </Link>

                {/* Register Button */}
                <Link
                  to="/register-selection"
                  className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
                >
                  {t('register')}
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-gray-700 hover:text-orange-600"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-lg font-medium text-gray-700 hover:text-orange-600 transition-colors ${
                    location.pathname === item.href ? 'text-orange-600 font-semibold' : ''
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              {!user && (
                <div className="flex flex-col space-y-2 pt-4 border-t">
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-orange-600 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('login')}
                  </Link>

                  {/* Mobile Register Button */}
                  <Link
                    to="/register-selection"
                    className="block bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('register')}
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
